import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import type { LoginCredentials, LoginResult } from "../api/authApi";
import { adminLogin, adminLogout, fetchCurrentUser } from "../api/authApi";
import {
  authStatusAtom,
  authUserAtom,
  type AuthUser,
} from "../atoms/authAtoms";

/**
 * Cookie-based auth state. The HttpOnly `eriol_session_admin` cookie holds
 * the credential — JS can't read it, so we answer "are we logged in?" by
 * calling /auth/me on mount. A 200 means yes (and gives us the user), 401
 * means no.
 *
 * For backward compatibility with components that read `session.user`, we
 * expose the user atom shaped as `{ user }` under the `session` key.
 */
export const useAuth = () => {
  const [user, setUser] = useAtom(authUserAtom);
  const [status, setStatus] = useAtom(authStatusAtom);
  const [error, setError] = useState<string | null>(null);
  const bootstrappedRef = useRef(false);

  // Bootstrap: on mount, ask the backend who we are. If the cookie is valid,
  // /me succeeds and we transition to authenticated; otherwise unauthenticated.
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    fetchCurrentUser()
      .then((fetched) => {
        setUser(fetched);
        setStatus("authenticated");
      })
      .catch(() => {
        setUser(null);
        setStatus("unauthenticated");
      });
  }, [setStatus, setUser]);

  const signIn = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResult> => {
      setStatus("checking");
      setError(null);
      try {
        const result = await adminLogin(credentials);

        if ("requiresTwoFactor" in result) {
          setStatus("unauthenticated");
          return result;
        }

        // Backend set eriol_session_admin + eriol_csrf cookies on the
        // response; we just keep the user profile in atom.
        setUser(result.user);
        setStatus("authenticated");
        return result;
      } catch (signInError) {
        setUser(null);
        setStatus("unauthenticated");
        setError(
          signInError instanceof Error
            ? signInError.message
            : "Fallo de autenticación",
        );
        throw signInError;
      }
    },
    [setStatus, setUser],
  );

  const signOut = useCallback(async () => {
    try {
      await adminLogout();
    } catch {
      // best-effort — local state wiped below regardless
    }
    setUser(null);
    setStatus("unauthenticated");
    setError(null);
  }, [setStatus, setUser]);

  const updateSessionUser = useCallback(
    (patch: Partial<AuthUser>) => {
      setUser((previous) => (previous ? { ...previous, ...patch } : previous));
    },
    [setUser],
  );

  return {
    session: { user },
    isAuthenticated: status === "authenticated",
    isLoading: status === "checking",
    error,
    signIn,
    signOut,
    updateSessionUser,
  };
};
